import type { PropertyValueResource } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";

type Status = "new" | "deleted";

export interface CommonTemplateEntry {
    value: PropertyValueResource | undefined;
    environmentIds: string[];
    status?: Status;
}

export function useCommonTemplatesChangeTracker(): CommonTemplatesChangeTracker {
    const [changes, setChanges] = useState<Map<CommonTemplatesKey, CommonTemplateEntry>>(new Map());

    return {
        has: (key: CommonTemplatesKey) => changes.has(key),
        get: (key: CommonTemplatesKey) => {
            if (changes.has(key)) {
                return changes.get(key);
            }

            throw new Error(`Key "${key}" not found in changed common templates`);
        },
        set: (key: CommonTemplatesKey, entry: CommonTemplateEntry) => {
            const copy = new Map(changes);
            const existingEntry = copy.get(key);
            copy.set(key, { ...entry, status: existingEntry?.status });
            setChanges(copy);
        },
        add: (key: CommonTemplatesKey, entry: CommonTemplateEntry) => {
            const copy = new Map(changes);
            copy.set(key, { ...entry, status: "new" });
            setChanges(copy);
        },
        delete: (key: CommonTemplatesKey) => {
            const copy = new Map(changes);
            const existingEntry = copy.get(key);

            if (existingEntry?.status === "new") {
                copy.delete(key);
            } else {
                copy.set(key, { value: undefined, environmentIds: [], status: "deleted" });
            }

            setChanges(copy);
        },
        remove: (key: CommonTemplatesKey) => {
            const copy = new Map(changes);
            copy.delete(key);

            setChanges(copy);
        },
        reset: () => setChanges(new Map()),
        values: () =>
            Array.from(changes.entries()).map(([key, entry]) => {
                const { setId, templateId, tenantId, guid } = extractCommonTemplateIds(key);
                return {
                    id: guid,
                    setId,
                    templateId,
                    tenantId,
                    value: entry.value,
                    environmentIds: entry.environmentIds,
                    status: entry.status,
                };
            }),
        hasAny: () => changes.size > 0,
        changedTenants: () => {
            const tenantIds: Set<string> = new Set();
            for (const variableKey of changes.keys()) {
                const { tenantId } = extractCommonTemplateIds(variableKey);
                tenantIds.add(tenantId);
            }

            return tenantIds;
        },
        newVariables: (setId: string, templateId: string, tenantId: string) => {
            const filteredMap = new Map<CommonTemplatesKey, CommonTemplateEntry>();

            for (const [key, entry] of changes.entries()) {
                if (key.startsWith(`${setId};${templateId};${tenantId}`) && entry.status === "new") {
                    filteredMap.set(key, entry);
                }
            }

            return filteredMap;
        },
        removedVariables: () => {
            const filteredMap = new Map<CommonTemplatesKey, CommonTemplateEntry>();

            for (const [key, entry] of changes.entries()) {
                if (entry.status === "deleted") {
                    filteredMap.set(key, entry);
                }
            }

            return filteredMap;
        },
        hasValueChanged: (key: CommonTemplatesKey, originalValue: PropertyValueResource | undefined) => {
            if (!changes.has(key)) return false;
            const trackedEntry = changes.get(key);
            return trackedEntry?.value !== originalValue;
        },
        hasScopeChanged: (key: CommonTemplatesKey, originalEnvironmentIds: string[]) => {
            if (!changes.has(key)) return false;
            const trackedEntry = changes.get(key);
            return !areArraysEqual(trackedEntry?.environmentIds, originalEnvironmentIds);
        },
    };
}

export interface CommonTemplatesChangeTracker {
    has: (key: CommonTemplatesKey) => boolean;
    get: (key: CommonTemplatesKey) => CommonTemplateEntry | undefined;
    set: (key: CommonTemplatesKey, entry: CommonTemplateEntry) => void;
    add: (key: CommonTemplatesKey, entry: CommonTemplateEntry) => void;
    delete: (key: CommonTemplatesKey) => void;
    remove: (key: CommonTemplatesKey) => void;
    reset: () => void;
    values: () => ChangedCommonTemplate[];
    hasAny: () => boolean;
    changedTenants: () => Set<string>;
    newVariables: (setId: string, templateId: string, tenantId: string) => Map<CommonTemplatesKey, CommonTemplateEntry>;
    removedVariables: () => Map<CommonTemplatesKey, CommonTemplateEntry>;
    hasValueChanged: (key: CommonTemplatesKey, originalValue: PropertyValueResource | undefined) => boolean;
    hasScopeChanged: (key: CommonTemplatesKey, originalEnvironmentIds: string[]) => boolean;
}

export interface ChangedCommonTemplate {
    id: string;
    setId: string;
    templateId: string;
    tenantId: string;
    value: PropertyValueResource | undefined;
    environmentIds: string[];
    status: Status | undefined;
}

export type CommonTemplatesKey = `${string};${string};${string};${string}`;

export function createCommonTemplatesKey(setId: string, templateId: string, tenantId: string, guid: string): CommonTemplatesKey {
    return `${setId};${templateId};${tenantId};${guid}`;
}

export function extractCommonTemplateIds(key: CommonTemplatesKey): { setId: string; templateId: string; tenantId: string; guid: string } {
    const extracted = key.split(`;`);
    return {
        setId: extracted[0],
        templateId: extracted[1],
        tenantId: extracted[2],
        guid: extracted[3],
    };
}

export function areArraysEqual(a: string[] | undefined, b: string[] | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;

    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    return sortedA.every((val, index) => val === sortedB[index]);
}
